import React, { FC, useState } from 'react';
import styles from './FeedbackModal.module.scss'
import { IHotel, IPlace, IRestaurant } from "@common/redux/types/places";
import { IEvent } from "@common/redux/types/event";
import { Modal } from "@kursk/components/ui/Modal/Modal";
import { Textarea } from "@kursk/components/ui/Textarea/Textarea";
import { Rating } from '../../Rating/Rating';
import classNames from "classnames";
import { IArticle } from "@common/redux/types/articles";
import { IStream } from "@common/redux/types/invoices";
import { Button } from "@kursk/components/ui/Button/Button";
import { EntityType, IUser } from "@common/redux/types/common";
import { useDispatchThunk } from "@common/redux/dispatchThunk";
import { createFeedback } from "@common/redux/slices/feedbacks.slice";
import { IFeedbackRequest, IFeedbackTag } from "@common/redux/types/feedbacks";
import { defaultFeedbackTags, feedbackHotelTags, feedbackRestaurantTags } from "@kursk/utils/hashMapFeedbackTags";
import { prepareEntity } from "@kursk/utils/prepareFeedbackEntity";
import { IExcursion } from '@common/redux/types/excursion';
import { ITour } from '@common/redux/types/tour';

interface Props {
  entity: IEvent | IArticle | IPlace | IHotel | IRestaurant | IStream | IExcursion | ITour;
  entityType: EntityType;
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  user: IUser;
  setIsSuccessModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export const FeedbackModal: FC<Props> = ({
  entity,
  entityType,
  isOpen,
  setIsOpen,
  user,
  setIsSuccessModalOpen,
}) => {
  const dispatchThunk = useDispatchThunk();
  const [rating, setRating] = useState<number>(0);
  const [selectedVariant, setSelectedVariant] = useState<IFeedbackTag[]>([]);
  const [description, setDescription] = useState<string>('');
  const variantsTags = ((): IFeedbackTag[] => {
    if (entityType === 'hotels') {
      return feedbackHotelTags;
    }
    if (entityType === 'restaurants') {
      return feedbackRestaurantTags;
    }
    return defaultFeedbackTags;
  })();

  const selectRating = (rating: number) => {
    if (selectedVariant.length) {
      const selectedArr = [...selectedVariant];
      setSelectedVariant(selectedArr.filter((variant) =>
        rating >= variant.minRating && rating <= variant.maxRating));
    }
    setRating(rating);
  };

  const selectVariant = (variant: IFeedbackTag) => {
    const selectIndex = selectedVariant.findIndex((item) => item.name === variant.name);
    if (selectIndex === -1) {
      return setSelectedVariant([...selectedVariant, variant]);
    } else {
      const arr = [...selectedVariant];
      arr.splice(selectIndex, 1);
      return setSelectedVariant(arr);
    }
  };

  const sendData = async () => {
    const tags = [];
    selectedVariant.forEach((variant) => tags.push(variant.originName));
    const data: IFeedbackRequest = {
      userId: user._id,
      description,
      entity: prepareEntity(entityType, entity._id),
      value: rating,
      tags,
    };
    const res = await dispatchThunk(createFeedback(data));
    if (!res.error) {
      setRating(0);
      setDescription('');
      setSelectedVariant([]);
      setIsSuccessModalOpen(true);
      setIsOpen(false);
    }
  };

  const getEntityType = (about: boolean) => {
    if (entityType === 'places' || entityType === 'hotels' || entityType === 'restaurants') {
      return about ? 'место' : 'месте';
    }
    if (entityType === 'events') return about ? 'событие' : 'событии';
    if (
      entityType === 'overviews' ||
      entityType === 'news' ||
      entityType === 'touristroutes'
    ) return about ? 'материал' : 'материале';
    if (entityType === 'streams') return about ? 'трансляцию' : 'трансляции';
  };

  const stringifyName = () => {
    let formattedText = entity.name;
    if (entity.name[0].charCodeAt(0) !== 171) {
      formattedText = '«' + formattedText;
    }
    if (entity.name[entity.name.length - 1].charCodeAt(0) !== 187) {
      formattedText += '»';
    }
    return formattedText;
  };

  const disabledTag = (variant: IFeedbackTag): boolean => {
    return !(rating >= variant.minRating && rating <= variant.maxRating) ||
      rating === 0 || !!selectedVariant.find((tag) => tag?.disabled)
      ?.disabled.find((tagOriginName) => tagOriginName === variant.originName);
  };

  const handleClose = () => {
    setRating(0);
    setDescription('');
    setSelectedVariant([]);
    setIsOpen(false);
  }

  return (
    <Modal isOpen={isOpen} onRequestClose={handleClose} classNameWrapper={styles.wrapper}>
      <div className={styles.header}>
        <div className={styles.title}>
            Оставить отзыв о {getEntityType(false)} {stringifyName()}
        </div>
      </div>
      <div className={styles.body}>
        <div className={styles.ratingDescription}>
          <div className={styles.label}>
            Оцените {getEntityType(true)}
            <span className={styles.require}>*</span>
          </div>
          <div className={styles.rating}>
            <Rating
              rating={rating}
              setRating={selectRating}
              count={5}
              starSize={24}
              noSelectedColorStar='#e5e5e5'
              withoutNumber
              starSelect
            />
          </div>
        </div>
        <div className={styles.tagsDescriptions}>
          <div className={styles.label}>
            Выберите вариант
            <span className={styles.require}>*</span>
          </div>
          <div className={styles.tags}>
            {variantsTags.map((variant) => (
              <button
                key={variant.originName}
                className={classNames([
                  styles.tag,
                  selectedVariant.findIndex((item) => item.name === variant.name) !== -1 && styles.selected
                ])}
                onClick={() => selectVariant(variant)}
                disabled={disabledTag(variant)}
              >
                <span>{variant.name}</span>
              </button>
            ))}
          </div>
        </div>
        <div className={styles.feedbackDescription}>
          <div className={styles.feedbackDescriptionLabel}>
            <div className={styles.label}>
                Расскажите о {getEntityType(false)}
            </div>
            <div className={styles.label}>{description.length}/1000</div>
          </div>
          <div>
            <Textarea
              rows={100}
              onChange={(e) => setDescription(e.target.value)}
              error={description.length > 1000 && 'Введите не более 1000 символов'}/>
          </div>
        </div>
      </div>
      <div className={styles.footer}>
        <Button
          text='Отменить'
          buttonType='ghostButton'
          onClick={handleClose}
        />
        <Button
          text='Отправить'
          buttonType='greenPrimary'
          onClick={sendData}
          disabled={rating === 0 || selectedVariant.length === 0 || description.length > 1000}
        />
      </div>
    </Modal>
  );
};
